import { create } from 'zustand';

import AsyncStorage from '@react-native-async-storage/async-storage'

import { AccessibleDatabase, SessionStore, Authentification } from './sessionStore.types';
import { Login } from '../../api/Login/types';
import { navigation } from '../Navigation/Navigation.component';
import { LoginApi } from '../../api/Login/LoginApi';
import { TenantApi } from '../../api/Tenant/TenantApi';
import { activeRequests, axiosInstance } from '../../api/Middleware';
import { Platform } from 'react-native';

const DEVICE_IDENTIFIER_STORAGE_KEY = 'deviceIdentifier';
const DEVICE_IDENTIFIER_MAX_LENGTH = 20;

const simpleHash = (value: string): string => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) || 0;
  }
  return Math.abs(hash).toString(16);
};

const hashValue = async (value: string): Promise<string> => {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoded = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 24);
  }

  return simpleHash(value);
};

const compactIdentifier = async (rawValue: string, prefix: string): Promise<string> => {
  const hashed = await hashValue(rawValue);
  const safePrefix = prefix.slice(0, 1);
  const maxHashLength = Math.max(1, DEVICE_IDENTIFIER_MAX_LENGTH - safePrefix.length);
  return `${safePrefix}${hashed.slice(0, maxHashLength)}`;
};

const generateFallbackIdentifier = async (): Promise<string> => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const navigatorInfo = window.navigator;
    const screenInfo = window.screen;
    const source = [
      navigatorInfo.userAgent || '',
      navigatorInfo.platform || '',
      navigatorInfo.language || '',
      Array.isArray(navigatorInfo.languages) ? navigatorInfo.languages.join(',') : '',
      String(navigatorInfo.hardwareConcurrency || ''),
      String((navigatorInfo as any).deviceMemory || ''),
      screenInfo ? `${screenInfo.width}x${screenInfo.height}x${screenInfo.colorDepth}` : '',
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    ].join('|');

    return compactIdentifier(source, 'w');
  }

  // Dernier recours natif si DeviceInfo échoue exceptionnellement.
  return compactIdentifier(`${Platform.OS}-unknown-device`, 'n');
};

const getCurrentAgentIdentifier = (state: SessionStore): number | undefined => {
  const currentDatabase = state.accessibleDatabases.find(db =>
    db.tenant?.identifiant === state.session?.base?.tenant &&
    db.component?.identifiant === state.session?.base?.component
  );

  return currentDatabase?.identifiantAgentActuel;
};

const trySendNotificationTokenIfReady = async (): Promise<void> => {
  const state = useSessionStore.getState();
  const identifiantAgent = getCurrentAgentIdentifier(state);

  if (!identifiantAgent || !state.notificationToken?.trim()) {
    return;
  }

  await state.sendTokenToServer(identifiantAgent);
};


const getOrCreateDeviceIdentifier = async (): Promise<string> => {
  const existingIdentifier = await AsyncStorage.getItem(DEVICE_IDENTIFIER_STORAGE_KEY);
  if (existingIdentifier) {
    return existingIdentifier;
  }

  let deviceUniqueId = '';

  try {
    if (Platform.OS !== 'web') {
      const { default: DeviceInfo } = await import('react-native-device-info');
      const nativeId = String(await DeviceInfo.getUniqueId()).trim();
      if (nativeId) {
        deviceUniqueId = await compactIdentifier(nativeId, 'n');
      }
    }
  } catch (e) {
    // fallback géré juste en dessous
  }

  if (!deviceUniqueId) {
    deviceUniqueId = await generateFallbackIdentifier();
  }

  await AsyncStorage.setItem(DEVICE_IDENTIFIER_STORAGE_KEY, deviceUniqueId);
  return deviceUniqueId;
};

/**
 * Supprime les logos base64 des collectivités avant la persistance dans AsyncStorage
 * pour éviter l'erreur Android "Row too big to be fit into CursorWindow" (limite 2MB).
 * Les logos sont rechargés depuis l'API à chaque connexion.
 */
const stripLogosForStorage = (databases: AccessibleDatabase[]): AccessibleDatabase[] =>
  databases.map(db => ({
    ...db,
    fichesAgent: (db.fichesAgent ?? []).map(fiche => ({
      ...fiche,
      loginInfo: {
        ...fiche.loginInfo,
        // Supprimer imageProfil : peut être un URI local invalide après redémarrage
        // ou un grand base64. Il est re-fetché depuis l'API à chaque ouverture du Profil.
        imageProfil: null,
        // Ne pas persister des champs potentiellement volumineux ou sensibles
        iban: null,
        signature: null,
      },
      collectivites: fiche.collectivites?.map(({ logo: _logo, ...rest }) => ({
        ...rest,
        logo: null,
      })),
    })),
  }));

export const useSessionStore = create<SessionStore>((set) => ({
  accessibleDatabases: [],
  isLoading: false,
  loadingMessage: 'Chargement...',

  setSession: async (session: Authentification) => {
    set({ session });
    // Ne jamais persister le mot de passe en clair. Si l'objet session comporte
    // un champ user.password, l'écraser lors de la sauvegarde.
    const safeSession: Authentification = {
      ...session,
      user: session.user ? { ...session.user, password: '' } : session.user,
    };
    await AsyncStorage.setItem('userSession', JSON.stringify(safeSession));
    trySendNotificationTokenIfReady().catch(console.error);
  },

  setAccessibleDatabases: async (accessibleDatabases: AccessibleDatabase[]) => {
    set({ accessibleDatabases });
    // Sauvegarder sans les logos base64 pour éviter "Row too big" sur Android
    await AsyncStorage.setItem('accessibleDatabases', JSON.stringify(stripLogosForStorage(accessibleDatabases)));
    console.log('[SessionStore] AccessibleDatabases saved to AsyncStorage:', accessibleDatabases.length, 'databases');
    trySendNotificationTokenIfReady().catch(console.error);
  },

  selectDatabase: async (database: AccessibleDatabase, navigationObject) => {
    set((state) => {
      // Mettre à jour automatiquement la session utilisateur avec les infos de la database sélectionnée
      const newSession = {
        access_token: '',
        refresh_token: '',
        // Conserver les informations utiles mais s'assurer que password est
        // explicitement vidé (ne pas se fier au spread pour écraser password).
        user: {
          username: '',
          rememberMe: false,
          ...state.session?.user,
          password: '',
        },
        ...state.session,
        base: {
          tenant: database.tenant?.identifiant || state.session?.base?.tenant || 0,
          component: database.component?.identifiant || state.session?.base?.component || 0,
          nomTenant: database.tenant?.nom || state.session?.base?.nomTenant || '',
          nomComponent: database.component?.nom || state.session?.base?.nomComponent || ''
        }
      };

      // Sauvegarder la session mise à jour dans AsyncStorage (mot de passe vidé)
      AsyncStorage.setItem('userSession', JSON.stringify(newSession))
        .then(() => console.log('[SessionStore] Session saved to AsyncStorage'))
        .catch(error => console.error('[SessionStore] Error saving session:', error));

      return { session: newSession };
    });
    if(database.fichesAgent.length === 1){
      // Publier le contexte tenant/component sur la gateway avant de naviguer
      await TenantApi.setContext(
        database.tenant?.identifiant,
        database.component?.identifiant
      ).catch(error => console.warn('[SessionStore] setContext error:', error));

      // Si une seule fiche agent, la sélectionner automatiquement
      useSessionStore.getState().selectFicheAgent(database.fichesAgent[0].loginInfo.identifiant, navigationObject);
    } else {
      // Publier le contexte tenant/component sur la gateway avant de naviguer
      await TenantApi.setContext(
        database.tenant?.identifiant,
        database.component?.identifiant
      ).catch(error => console.warn('[SessionStore] setContext error:', error));

      navigation.navigate('SelectionFicheAgent', navigationObject);
    }
  },

  selectFicheAgent: async (identifiantAgent: number, navigationObject?) => {
    set((state) => {
      if (!state.session?.base?.tenant || !state.session?.base?.component) {
        return {};
      }

      const updatedDatabases = state.accessibleDatabases.map(db => {
        // Trouver la base actuellement active
        if (db.tenant?.identifiant === state.session?.base?.tenant &&
            db.component?.identifiant === state.session?.base?.component) {
          return {
            ...db,
            identifiantAgentActuel: identifiantAgent
          }
        }
        return db;
      });

      // Sauvegarder sans les logos base64 pour éviter "Row too big" sur Android
      AsyncStorage.setItem('accessibleDatabases', JSON.stringify(stripLogosForStorage(updatedDatabases)))
        .catch(console.error);
      state.sendTokenToServer(identifiantAgent).catch(console.error);
      return { accessibleDatabases: updatedDatabases };
    })
    // Après la sélection de la fiche agent, fiche collectivité, direction l'accueil
    navigation.navigate('HomeTabs', navigationObject, { screen: 'Accueil' });
  },

  setLoading: (loading: boolean, message = 'Chargement...') =>
    set({ isLoading: loading, loadingMessage: message }),

  clearSession: async (revokeToken = true) => {
    // Révoquer le token côté serveur si demandé
    if (revokeToken) {
      const state: SessionStore = useSessionStore.getState();
      if (state.session?.base?.tenant && state.session?.base?.component && state.session?.access_token) {
        try {
          await LoginApi.logout(state.session.base.tenant, state.session.base.component, state.session.access_token);
        } catch (e) {
          console.warn('[SessionStore] Erreur lors de la révocation du token:', e);
        }
      }
    }

    // Supprimer les données du storage
    AsyncStorage.removeItem('userSession').catch(console.error);
    AsyncStorage.removeItem('accessibleDatabases').catch(console.error);
    console.log('[SessionStore] Session and AccessibleDatabases cleared from AsyncStorage');

    return set({
      session: undefined,
      accessibleDatabases: [],
      isLoading: false
    });
  },

  updateUser: (userUpdate: Partial<Authentification['user']>) =>
    set((state) => ({
      session: state.session ? {
        ...state.session,
        user: { ...state.session.user, ...userUpdate }
      } : undefined
    })),

  // Méthode pour récupérer les loginInfo de la base actuellement sélectionnée
  getCurrentInfos: () => {
    const state: SessionStore = useSessionStore.getState();

    // Trouver la base actuellement active basée sur tenant et component de la session
    const currentDatabase = state.accessibleDatabases.find(db =>
      db.tenant?.identifiant === state.session?.base?.tenant &&
      db.component?.identifiant === state.session?.base?.component
    );

    const currentFiche = currentDatabase?.fichesAgent.find((fiche) => fiche.loginInfo.identifiant === currentDatabase.identifiantAgentActuel);

    const currentEntite = currentFiche?.collectivites?.find(({identifiantEntite}) => identifiantEntite === currentFiche?.loginInfo.identifiantEntiteDefautEsag);

    return { currentDatabase, currentEntite, currentFiche, currentLogin: currentFiche?.loginInfo  };
  },


  // Méthode pour mettre à jour les loginInfo de la base actuellement sélectionnée
  updateCurrentLoginInfo: async (loginInfoUpdate: Partial<Login>) => {
    const state: SessionStore = useSessionStore.getState();
    if (!state.session?.base?.tenant || !state.session?.base?.component) {
      console.warn('[SessionStore] Cannot update loginInfo - no database selected');
      return {};
    }

    const updatedDatabases = state.accessibleDatabases.map(db => {
      // Trouver la base actuellement active
      if (db.tenant?.identifiant === state.session?.base?.tenant &&
          db.component?.identifiant === state.session?.base?.component) {

        // Mettre à jour le premier loginInfo (il devrait n'y en avoir qu'un)
        if (db.fichesAgent && db.fichesAgent.length > 0) {
          return {
            ...db,
            fichesAgent: db.fichesAgent.map((fiche) => {
              if(fiche.loginInfo.identifiant === db.identifiantAgentActuel){
                return {
                  ...fiche,
                  loginInfo: {
                    ...fiche.loginInfo,
                    ...loginInfoUpdate
                  }
                }
              }
              return fiche;
            })
          }
        }
      }
      return db;
    });

    state.setAccessibleDatabases(updatedDatabases)
  },
  setNotificationToken: (token: string) => {
    const normalizedToken = token?.trim();
    if (!normalizedToken) {
      return;
    }

    set({ notificationToken: normalizedToken });

    const state = useSessionStore.getState();
    const identifiantAgent = getCurrentAgentIdentifier(state);
    if (identifiantAgent) {
      state.sendTokenToServer(identifiantAgent).catch(console.error);
    }
  },
  sendTokenToServer: async (identifiantAgent: number) => {
    if (!identifiantAgent) {
      return;
    }

    const notificationToken = useSessionStore.getState().notificationToken?.trim();
    if (!notificationToken) {
      return;
    }

    let identifiantAppareil = '';
    try {
      identifiantAppareil = await getOrCreateDeviceIdentifier();
    } catch (error) {
      console.error('[SessionStore] Unable to resolve device identifier for notification token', error);
      return;
    }

    if (!identifiantAppareil) {
      return;
    }

    await axiosInstance.post('/personnel/personnel/notifications/token/create-or-update', {
      identifiantAgent,
      identifiantAppareil,
      token: notificationToken,
      plateforme: Platform.OS,
    });
  }
}));


// Fonction pour charger les données au démarrage
export const loadStoredData = async () => {
  try {
    useSessionStore.getState().setLoading(true, 'Chargement des données...');

    // Charger la session (si une ancienne version avait stocké le mot de passe,
    // le nettoyer immédiatement pour ne jamais le réinjecter en mémoire)
    const storedSession = await AsyncStorage.getItem('userSession');
    if (storedSession) {
      const session = JSON.parse(storedSession);
      if (session?.user) {
        try {
          session.user.password = '';
        } catch (e) {
          // ignore
        }
      }
      // Utiliser set directement pour éviter de re-sauvegarder
      useSessionStore.setState({ session });
      console.log('[SessionStore] Session restored from AsyncStorage (password sanitized)');
    }

    // Charger les bases accessibles
    const storedDatabases = await AsyncStorage.getItem('accessibleDatabases');
    if (storedDatabases) {
      const accessibleDatabases = JSON.parse(storedDatabases);
      // Appliquer stripLogosForStorage à la lecture aussi : les données persistées
      // peuvent contenir d'anciens imageProfil (content:// ou file:// devenus invalides)
      // ou des logos base64 stockés avant l'introduction du strip.
      const stripped = stripLogosForStorage(accessibleDatabases);
      useSessionStore.setState({ accessibleDatabases: stripped });
      // Écraser la valeur stockée par la version nettoyée pour éviter de relire
      // ultérieurement des payloads volumineux (ex: signatureImage en base64)
      try {
        await AsyncStorage.setItem('accessibleDatabases', JSON.stringify(stripped));
        console.log('[SessionStore] AccessibleDatabases restored and rewritten (stripped) in AsyncStorage:', stripped.length, 'databases');
      } catch (err) {
        console.warn('[SessionStore] Failed to rewrite accessibleDatabases after strip:', err);
      }

      trySendNotificationTokenIfReady().catch(console.error);
    }

  } catch (error) {
    console.error('Erreur lors du chargement des données stockées:', error);
  } finally {
    if(activeRequests === 0){
      useSessionStore.getState().setLoading(false);
    }
  }
};
